import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    Pagination,
    CircularProgress,
    Alert,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { format } from 'date-fns'
import { AvailableShift, GetAvailableShiftsParams } from '@/core/models/planning.model'
import ShiftCard from './ShiftCard'

interface AvailableShiftsListProps {
    shifts: AvailableShift[]
    isLoading: boolean
    error: any
    totalItems: number
    page: number
    limit: number
    onPageChange: (page: number) => void
    onFilterChange: (filters: Partial<GetAvailableShiftsParams>) => void
    onClaimShift: (shift: AvailableShift) => void
    filters: Partial<GetAvailableShiftsParams>
}

export function AvailableShiftsList({
    shifts,
    isLoading,
    error,
    totalItems,
    page,
    limit,
    onPageChange,
    onFilterChange,
    onClaimShift,
    filters
}: AvailableShiftsListProps) {
    const { t } = useTranslation()
    const [startDate, setStartDate] = useState<Date | null>(filters.startDate ? new Date(filters.startDate) : null)
    const [endDate, setEndDate] = useState<Date | null>(filters.endDate ? new Date(filters.endDate) : null)
    const [location, setLocation] = useState<string>(filters.location || '')
    const [reason, setReason] = useState<string>(filters.consultationReason || '')
    const [sortBy, setSortBy] = useState<string>(filters.sortBy || 'startTime')

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date)
        if (date) {
            onFilterChange({ ...filters, startDate: format(date, 'yyyy-MM-dd') })
        } else {
            const { startDate, ...rest } = filters
            onFilterChange(rest)
        }
    }

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date)
        if (date) {
            onFilterChange({ ...filters, endDate: format(date, 'yyyy-MM-dd') })
        } else {
            const { endDate, ...rest } = filters
            onFilterChange(rest)
        }
    }

    const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const location = event.target.value
        if (location) {
            onFilterChange({ ...filters, location })
        } else {
            const { location, ...rest } = filters
            onFilterChange(rest)
        }
        setLocation(location)
    }

    const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const consultationReason = event.target.value
        if (consultationReason) {
            onFilterChange({ ...filters, consultationReason })
        } else {
            const { consultationReason, ...rest } = filters
            onFilterChange(rest)
        }
        setReason(consultationReason)
    }

    const handleSortChange = (event: SelectChangeEvent) => {
        const value = event.target.value
        const [sortBy, sortDirection] = value.split('-') as [
            'startTime' | 'endTime' | 'location' | 'consultationReason',
            'asc' | 'desc'
        ]
        onFilterChange({ ...filters, sortBy, sortDirection })
        setSortBy(sortBy)
    }

    const totalPages = Math.ceil(totalItems / limit)

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value)
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return (
            <Alert severity="error">
                {t('page.shiftMarketplace.errorLoadingData', 'Error loading data. Please try again.')}
            </Alert>
        )
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Accordion defaultExpanded={false} sx={{ mb: 1 }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="filter-panel-content"
                    id="filter-panel-header"
                >
                    <Typography variant="subtitle1">
                        {t('page.shiftMarketplace.filters', 'Filters')}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('page.shiftMarketplace.startDate', 'Start Date')}
                                    value={startDate}
                                    onChange={(date: Date | null) => handleStartDateChange(date)}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth size="small" />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('page.shiftMarketplace.endDate', 'End Date')}
                                    value={endDate}
                                    onChange={(date: Date | null) => handleEndDateChange(date)}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth size="small" />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label={t('page.shiftMarketplace.location', 'Location')}
                                value={location}
                                onChange={handleLocationChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label={t('page.shiftMarketplace.consultationReason', 'Consultation Reason')}
                                value={reason}
                                onChange={handleReasonChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="sort-by-label">
                                    {t('page.shiftMarketplace.sortBy', 'Sort By')}
                                </InputLabel>
                                <Select
                                    labelId="sort-by-label"
                                    value={sortBy}
                                    label={t('page.shiftMarketplace.sortBy', 'Sort By')}
                                    onChange={handleSortChange}
                                >
                                    <MenuItem value="startTime">
                                        {t('page.shiftMarketplace.date', 'Date')}
                                    </MenuItem>
                                    <MenuItem value="availableSpots">
                                        {t('page.shiftMarketplace.availableSpotsSort', 'Available Spots')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Box sx={{ flexGrow: 1, overflow: 'auto', pb: 2 }}>
                {shifts.length === 0 ? (
                    <Alert severity="info">
                        {t('page.shiftMarketplace.noAvailableShiftsFound', 'No available shifts found for the selected criteria.')}
                    </Alert>
                ) : (
                    <Grid container spacing={2}>
                        {shifts.map((shift) => (
                            <Grid item xs={12} sm={6} md={4} key={`${shift.planningId}-${shift.shiftTemplateId}-${shift.startTime}`}>
                                <ShiftCard
                                    shift={shift}
                                    type="available"
                                    onAction={onClaimShift}
                                    actionDisabled={shift.availableSpots <= 0}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {shifts.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    )
}

export default AvailableShiftsList
